'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function MigrenTedavisindeBotoksPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/migren-tedavisinde-botoks-bilimsel-ve-klinik-yaklasim';
  const pageTitle = 'Migren Tedavisinde Botoks: Bilimsel ve Klinik Yaklaşım';
  const pageDescription = 'Botoks, migren tedavisinde nasıl etki eder? Kronik baş ağrısı yaşayan hastalarda botoks enjeksiyonu güvenli midir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Migren Tedavisinde Botoks', href: '/tr/blog/migren-tedavisinde-botoks-bilimsel-ve-klinik-yaklasim' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Migren tedavisinde botoks uygulaması ve bilimsel yaklaşım"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Nörolojik Tedavi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Migren Tedavisinde Botoks: Bilimsel ve Klinik Yaklaşım
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Kronik migren, yaşam kalitesini ciddi biçimde düşüren, ataklarla seyreden nörolojik bir hastalıktır. İlaç tedavisine yanıt vermeyen birçok hasta için botoks uygulaması, son yıllarda etkili bir tedavi alternatifi olarak kabul edilmiştir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                FDA tarafından 2010 yılında migren tedavisinde onaylanan botoks, bugün nöroloji kliniklerinde yaygın olarak kullanılmaktadır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Migren Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Migren, genellikle başın bir yarısında hissedilen, şiddetli, zonklayıcı baş ağrısı atakları ile karakterizedir. Buna genellikle bulantı, ışığa ve sese hassasiyet eşlik eder.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Migrenin ana nedenleri:
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-indigo-800 space-y-2">
                  <li>Genetik yatkınlık</li>
                  <li>Beyin damarlarının aşırı genişlemesi</li>
                  <li>Sinir uçlarında inflamatuvar kimyasalların salınımı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoks Migreni Nasıl Tedavi Eder?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum toksin, sinir uçlarında ağrı iletimini sağlayan nörotransmiterlerin salınımını engeller. Yani sadece kasları gevşetmekle kalmaz, ağrı sinyallerini bloke eder.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Etki mekanizması:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kas gerginliği azalır</li>
                  <li>Ağrı sinyalleri baskılanır</li>
                  <li>Sinir uçları sakinleşir</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  Sonuç: Atak sıklığı ve şiddeti azalır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Bölgeleri
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Amaç
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Nokta Sayısı
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Alın ve şakak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Gerilim ve migren kaynağı kaslar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">8–10</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Ense ve boyun</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Gerilme ağrılarını azaltmak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–8</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Baş arkası (oksipital bölge)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sinir basısını azaltmak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Omuz üstü (trapez)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas gevşemesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium">
                  Toplamda 30–35 enjeksiyon noktası uygulanır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etki Süresi
              </h2>
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Botoks migren tedavisinde 10–14 gün içinde etkisini gösterir.</li>
                  <li>Atak sıklığı ve ağrı şiddeti %50–70 oranında azalır.</li>
                  <li>Kalıcılık süresi ortalama 3–6 ay arasındadır.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Hangi Hastalarda Etkilidir?
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium mb-3">✅ Etkilidir:</p>
                <ul className="list-disc list-inside text-green-800 space-y-1">
                  <li>Ayda 15 günden fazla migren atağı olanlarda</li>
                  <li>Gerilim tipi baş ağrısıyla birlikte seyreden vakalarda</li>
                  <li>İlaç tedavisine yanıt vermeyen hastalarda</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 font-medium mb-3">⚠️ Uygun değildir:</p>
                <ul className="list-disc list-inside text-red-800 space-y-1">
                  <li>Gebelik ve emzirme döneminde</li>
                  <li>Kas hastalıkları veya nörolojik bozukluklarda</li>
                  <li>Enjeksiyon bölgesinde enfeksiyon olanlarda</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Migren Tedavisinde Botoksun Avantajları
              </h2>
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>İlaç yan etkisi yoktur</li>
                  <li>Uzun süreli ağrı kontrolü sağlar</li>
                  <li>Uyku düzenini iyileştirir</li>
                  <li>Konsantrasyonu artırır</li>
                  <li>Günlük yaşam kalitesini yükseltir</li>
                </ul>
                <p className="text-yellow-800 mt-3 font-medium">
                  💬 Botoks, migreni susturur; sadece başı değil, hayatı da rahatlatır.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Migren tedavisinde botoks uygulaması ve bilimsel yaklaşım"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Migren için botoks kimlere yapılır?</h3>
                  <p className="text-gray-700">Kronik migren tanısı almış ve ayda 15+ gün ağrı yaşayan hastalara.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi hemen başlar mı?</h3>
                  <p className="text-gray-700">Hayır, 7–10 gün içinde fark edilir, 2 haftada maksimum etkiye ulaşır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoks ağrılı mıdır?</h3>
                  <p className="text-gray-700">Hayır. İnce iğnelerle yapılır, işlem 15–20 dakika sürer.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Ne sıklıkla yapılmalı?</h3>
                  <p className="text-gray-700">Ortalama 4–6 ayda bir tekrarlanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Kalıcı çözüm sağlar mı?</h3>
                  <p className="text-gray-700">Uzun vadede ağrı sıklığını ve yoğunluğunu kalıcı olarak azaltabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>FDA (2010) – "Botox Approved for Chronic Migraine Prevention"</li>
                <li>American Academy of Neurology (AAN) – "Guidelines for Botulinum Toxin in Migraine"</li>
                <li>Harvard Health (2024) – "Botox for Chronic Migraine Relief"</li>
                <li>Türk Nöroloji Derneği (TND) – "Kronik Migren Tedavi Protokolü"</li>
                <li>ISAPS Global Medical Report 2024 – "Therapeutic Applications of Botulinum Toxin"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#migren</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#nöroloji</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#başagrısı</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#migrenbotoksu</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#yaşamkalitesi</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Migren Tedavisi Hakkında Bilgi Alın</h3>
              <p className="mb-6">Kronik migren ve botoks tedavisi hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Migren tedavisinde botoks hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dis-gicirdatma-bruksizm-tedavisinde-botoks"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Diş Gıcırdatma (Bruksizm) Tedavisinde Botoks</h4>
              <p className="text-gray-600 text-sm">Diş gıcırdatma ve çene sıkma sorunlarınız için botoks tedavisi hakkında detaylı bilgi.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
